import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import App from '@/app/App';
import '@/index.css';
import * as Sentry from '@sentry/browser';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { AppProvider } from '@/app/context/AppProvider';
import { AuthProvider } from '@/modules/auth/context/AuthProvider';
import { initPostHog } from '@/services/analytics/utils/posthogLazy.js';

// Import debug utilities in development
if (import.meta.env.VITE_PUBLIC_APP_ENV !== 'production') {
  import('@/modules/core/debugUtils');
}

Sentry.init({
  dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
  environment: import.meta.env.VITE_PUBLIC_APP_ENV,
  initialScope: {
    tags: {
      type: 'frontend',
    }
  }
});

// Vercel Analytics will be included via React component

// Add CodeAsk widget in development only
if (import.meta.env.VITE_PUBLIC_APP_ENV === 'development') {
  const codeAskScript = document.createElement('script');
  codeAskScript.src = 'https://dev.codeask.ai/widget.js';
  codeAskScript.onload = function() {
    // Initialize CodeAsk widget after script loads
    if (window.CodeAsk) {
      window.CodeAsk.init({
        appId: '11b0fa28-6f95-42a1-82bd-e0b820aa55bf',
        
        // UI Customization (Optional)
        position: 'bottom-right' // Options: bottom-left, bottom-right (default)
      });
    }
  };
  document.head.appendChild(codeAskScript);
}

// PWA support is handled by vite-plugin-pwa
import { registerChunkLoadRecovery } from '@/modules/core/chunkLoadRecovery';
registerChunkLoadRecovery();

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Router>
      <AuthProvider>
        <AppProvider>
          <App />
          <Analytics />
          <SpeedInsights />
        </AppProvider>
      </AuthProvider>
    </Router>
  </React.StrictMode>
);

// Fetch PostHog after first paint — analytics must never delay interactivity.
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => initPostHog(), { timeout: 5000 });
  } else {
    setTimeout(() => initPostHog(), 2000);
  }
}